import Box from 'components/shared/Box'
import moment from 'moment'
import { useIntl } from 'react-intl'
import { GetCartItemPresenter } from 'store/api-slice/hooks'
import { useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'
import {
    busTicketsSorting,
    cabanasSorting,
    mergeCityBreakTickets,
    mergeTickets,
    mergeTicketsBundles,
    sumTicketsBundlesDates,
    sumTicketsDates,
} from 'components/global/Summary/helpers'
import { SummaryBox } from 'components/global/Summary/components/SummaryBox'
import { SummaryGiftCardsList } from './SummaryGiftCardsList'

const ProductList = () => {
    const { formatMessage } = useIntl()
    const { language, isCreatorAccommodation } = useApp()
    const { items } = useAppSelector((state) => state.cart)
    const {
        tickets,
        ticketsBundles,
        ticketsCityBreak,
        spa,
        busTickets,
        cabanas,
        accommodation,
        accommodationDetails,
        marchVouchers,
    } = items

    const formatAccommodationDates = (dates: string[]) => {
        const start = moment(dates[0])
        const end = moment(dates[dates.length - 1])

        const formattedDates = []

        for (let current = start; current <= end; current.add(1, 'd')) {
            formattedDates.push(current.format('YYYY-MM-DD'))
        }
        return formattedDates
    }

    const accommodationSummaryBox = () => {
        if (!accommodation || !accommodationDetails) return

        const {
            totalPrice,
            totalPriceWithoutDiscount,
            hasPromotion,
            detail: { dateFrom, dateTo, rooms },
        } = accommodation

        // in case when general cart data are already updated,
        // but accommodation details from api are not updated yet
        if (rooms?.length !== accommodationDetails.length) return

        const items = accommodationDetails.map((item) => ({
            name: item.rateName,
            description: item.occupancyName,
            unitPrice: item.totalPrice,
            rooms,
        }))

        return (
            <SummaryBox
                dates={formatAccommodationDates([dateFrom, dateTo])}
                title={formatMessage({
                    id: 'global.summary.section_caption_accommo',
                    defaultMessage: 'Suntago Village',
                })}
                items={items}
                totalPrice={totalPrice}
                totalPriceWithoutDiscount={totalPriceWithoutDiscount || 0}
                stepToEdit={'accommodation'}
                hasPromotion={!!hasPromotion}
                rooms={rooms}
                shouldCountNights
            />
        )
    }

    return (
        <Box gap={32} isFullWidth>
            {isCreatorAccommodation && accommodationSummaryBox()}

            {(tickets.length > 0 || ticketsBundles.length > 0) && (
                <>
                    <SummaryBox
                        dates={
                            tickets.length
                                ? sumTicketsDates(tickets)
                                : sumTicketsBundlesDates(ticketsBundles)
                        }
                        title={formatMessage({
                            id: 'global.summary.section_caption_tickets',
                            defaultMessage: 'Bilety do parku',
                        })}
                        items={[
                            ...mergeTicketsBundles({
                                ticketsBundles,
                                language,
                            }),
                            ...mergeTickets(tickets),
                        ]}
                        stepToEdit={'tickets'}
                    />
                </>
            )}
            {!!ticketsCityBreak.length && (
                <SummaryBox
                    dates={sumTicketsDates(ticketsCityBreak)}
                    title={formatMessage({
                        id: 'global.summary.section_caption_city_break',
                        defaultMessage: '_',
                    })}
                    items={mergeCityBreakTickets(ticketsCityBreak)}
                    stepToEdit={'tickets'}
                />
            )}
            {!!(spa.length || cabanas.length || busTickets.length) && (
                <SummaryBox
                    title={formatMessage({
                        id: 'global.summary.section_caption_upsells',
                        defaultMessage: 'Udogodnienia i atrakcje',
                    })}
                    items={[
                        ...spa,
                        ...busTicketsSorting(busTickets),
                        ...(cabanasSorting(cabanas) as GetCartItemPresenter[]), // TODO: unify types for cabanas
                    ]}
                    stepToEdit={'upsell'}
                />
            )}
            {marchVouchers.length > 0 && (
                <SummaryBox
                    title={formatMessage({
                        id: 'promo.march.badge',
                        defaultMessage: 'Marcowa Promocja',
                    })}
                    items={marchVouchers}
                    stepToEdit={'tickets'}
                />
            )}

            {!isCreatorAccommodation && accommodationSummaryBox()}
            <SummaryGiftCardsList />
        </Box>
    )
}

export default ProductList
